import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { catalogApi, type Catalog, type CategoryNode } from '../../api/catalog';
import { CategoryTreeView } from './CategoryTreeView';

export function TaxonomyExplorerPage() {
  const [catalogs, setCatalogs] = useState<Catalog[]>([]);
  const [catalogId, setCatalogId] = useState('');
  const [tree, setTree] = useState<CategoryNode[]>([]);
  const [query, setQuery] = useState('');
  const [flat, setFlat] = useState<{ id: string; name: string; path: string; depth: number }[]>([]);
  const [error, setError] = useState('');

  useEffect(() => {
    void catalogApi.listCatalogs().then((items) => {
      setCatalogs(items);
      if (items[0]?.id) setCatalogId(items[0].id);
    });
  }, []);

  useEffect(() => {
    if (!catalogId) return;
    void catalogApi
      .getCategoryTree(catalogId)
      .then((nodes) => {
        setTree(nodes);
        setFlat(flatten(nodes));
      })
      .catch((e) => setError(String(e)));
  }, [catalogId]);

  const filtered = query.trim()
    ? flat.filter((c) => c.name.toLowerCase().includes(query.toLowerCase()) || c.path.toLowerCase().includes(query.toLowerCase()))
    : flat;

  return (
    <div className="mx-auto max-w-6xl p-6">
      <Link to="/catalog" className="text-sm text-slate-600 hover:underline">← Catalogs</Link>
      <h1 className="mt-4 text-2xl font-semibold">Taxonomy Explorer</h1>
      <p className="mt-1 text-sm text-slate-600">Browse and search hierarchical taxonomies across catalogs.</p>
      <div className="mt-4 flex flex-wrap gap-3">
        <select value={catalogId} onChange={(e) => setCatalogId(e.target.value)} className="rounded-lg border px-3 py-2 text-sm">
          {catalogs.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search categories…"
          className="min-w-[200px] flex-1 rounded-lg border px-3 py-2 text-sm"
          aria-label="Search categories"
        />
      </div>
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <div className="rounded-xl border bg-white p-4">
          <h2 className="font-medium">Tree view</h2>
          <div className="mt-3">{tree.length ? <CategoryTreeView nodes={tree} /> : <p className="text-slate-500">Empty taxonomy.</p>}</div>
        </div>
        <div className="rounded-xl border bg-white p-4">
          <h2 className="font-medium">Search results ({filtered.length})</h2>
          <ul className="mt-3 max-h-96 space-y-1 overflow-y-auto text-sm">
            {filtered.map((c) => (
              <li key={c.id} className="rounded px-2 py-1 hover:bg-slate-50">
                <Link to={`/catalog/categories/${c.id}/edit`} className="font-medium hover:underline">{c.name}</Link>
                <span className="ml-2 text-slate-400">{c.path}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function flatten(nodes: CategoryNode[], acc: { id: string; name: string; path: string; depth: number }[] = []): typeof acc {
  for (const n of nodes) {
    acc.push({ id: n.category.id, name: n.category.name, path: n.category.path, depth: n.category.depth });
    if (n.children?.length) flatten(n.children, acc);
  }
  return acc;
}
